import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';

const REMEMBER_ME_STORAGE_KEY = '@sendifier:remember-me';
const TRUST_DEVICE_STORAGE_KEY = '@sendifier:trust-device';
const STORAGE_VERSION = 1;

type StorageDriver = 'secure' | 'async';

export interface RememberedCredentials {
  email: string;
  password: string;
  savedAt: string;
  version: number;
}

let resolvedDriver: StorageDriver | null = null;
let driverCheckPromise: Promise<StorageDriver> | null = null;

async function getPreferredDriver(): Promise<StorageDriver> {
  if (resolvedDriver) {
    return resolvedDriver;
  }

  if (!driverCheckPromise) {
    driverCheckPromise = (async () => {
      try {
        if (SecureStore?.isAvailableAsync && (await SecureStore.isAvailableAsync())) {
          resolvedDriver = 'secure';
          return 'secure';
        }
      } catch (error) {
        console.warn('[credentialManager] SecureStore not available, falling back to AsyncStorage:', error);
      }

      resolvedDriver = 'async';
      return 'async';
    })();
  }

  return driverCheckPromise;
}

async function setItem(value: string): Promise<void> {
  const driver = await getPreferredDriver();
  if (driver === 'secure' && SecureStore?.setItemAsync) {
    await SecureStore.setItemAsync(REMEMBER_ME_STORAGE_KEY, value, {
      keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY,
    });
    return;
  }

  await AsyncStorage.setItem(REMEMBER_ME_STORAGE_KEY, value);
}

async function getItem(): Promise<string | null> {
  const driver = await getPreferredDriver();
  if (driver === 'secure' && SecureStore?.getItemAsync) {
    return SecureStore.getItemAsync(REMEMBER_ME_STORAGE_KEY);
  }

  return AsyncStorage.getItem(REMEMBER_ME_STORAGE_KEY);
}

async function deleteItem(): Promise<void> {
  const driver = await getPreferredDriver();
  if (driver === 'secure' && SecureStore?.deleteItemAsync) {
    await SecureStore.deleteItemAsync(REMEMBER_ME_STORAGE_KEY);
    return;
  }

  await AsyncStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
}

export async function saveCredentials(email: string, password: string): Promise<void> {
  if (!email || !password) {
    await clearCredentials();
    return;
  }

  const payload: RememberedCredentials = {
    email: email.trim(),
    password,
    savedAt: new Date().toISOString(),
    version: STORAGE_VERSION,
  };

  try {
    await setItem(JSON.stringify(payload));
  } catch (error) {
    console.error('[credentialManager] Failed to save credentials:', error);
  }
}

export async function loadCredentials(): Promise<RememberedCredentials | null> {
  try {
    const raw = await getItem();
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as RememberedCredentials;
    if (!parsed?.email || !parsed?.password) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[credentialManager] Failed to load credentials:', error);
    return null;
  }
}

export async function clearCredentials(): Promise<void> {
  try {
    await deleteItem();
  } catch (error) {
    console.error('[credentialManager] Failed to clear credentials:', error);
  }
}

export async function hasRememberedCredentials(): Promise<boolean> {
  const creds = await loadCredentials();
  return !!creds;
}

/**
 * "Remember this device" intent, set at login time and consumed once by the next
 * selectBusiness() call (the phase-2 call that actually mints the session's refresh
 * token) — selectBusiness() reads and clears it so a later login on the same device
 * without the checkbox doesn't inherit a stale "trusted" flag.
 */
export async function setTrustDeviceIntent(trust: boolean): Promise<void> {
  try {
    if (trust) {
      await AsyncStorage.setItem(TRUST_DEVICE_STORAGE_KEY, '1');
    } else {
      await AsyncStorage.removeItem(TRUST_DEVICE_STORAGE_KEY);
    }
  } catch (error) {
    console.error('[credentialManager] Failed to set trust-device intent:', error);
  }
}

export async function consumeTrustDeviceIntent(): Promise<boolean> {
  try {
    const value = await AsyncStorage.getItem(TRUST_DEVICE_STORAGE_KEY);
    await AsyncStorage.removeItem(TRUST_DEVICE_STORAGE_KEY);
    return value === '1';
  } catch (error) {
    console.error('[credentialManager] Failed to consume trust-device intent:', error);
    return false;
  }
}

